/**
 * Bid arithmetic — pure, no I/O (§4.3 "show the arithmetic").
 * Ordering preserved exactly from the v3 prototype (regression-fixed by B-1001/B-1002):
 * per-line rounding → subtotal → markup → adjustment → tax → total → deposit.
 */
import { roundCents } from '../core/money';
import type { BidMath, FrozenLine, JobFin, PriceItem, TakeoffLine } from '../core/types';

export interface ResolvedLine {
  takeoffLineId: string;
  name: string;
  label?: string;
  qty: number;
  unit: string;
  rateCents: number;
  source: FrozenLine['source'];
  verified: boolean;
}

/** join takeoff lines to the price book; custom lines carry their own rate */
export function resolveLines(lines: TakeoffLine[], priceItems: PriceItem[]): ResolvedLine[] {
  const bySlug = new Map(priceItems.map((p) => [p.itemId, p]));
  const out: ResolvedLine[] = [];
  for (const l of lines) {
    if (l.deletedAt) continue;
    if (l.custom) {
      out.push({
        takeoffLineId: l.id, name: l.custom.name, label: l.label || undefined,
        qty: l.qty, unit: l.custom.unit,
        rateCents: l.unitPriceCentsOverride ?? 0,
        source: l.provenance.source, verified: l.verification.verified,
      });
      continue;
    }
    const p = l.priceItemId ? bySlug.get(l.priceItemId) : undefined;
    if (!p) continue; // orphaned reference: skipped, surfaced by the UI, never guessed
    out.push({
      takeoffLineId: l.id, name: p.name, label: l.label || undefined,
      qty: l.qty, unit: p.unit,
      rateCents: l.unitPriceCentsOverride ?? p.rateCents,
      source: l.provenance.source, verified: l.verification.verified,
    });
  }
  return out;
}

export function computeBid(lines: ResolvedLine[], fin: JobFin): { lines: FrozenLine[]; math: BidMath } {
  const frozen: FrozenLine[] = lines.map((l) => ({
    name: l.name, label: l.label, qty: l.qty, unit: l.unit, rateCents: l.rateCents,
    amountCents: roundCents(l.qty * l.rateCents),
    source: l.source, verified: l.verified,
  }));
  const subtotalCents = frozen.reduce((s, l) => s + l.amountCents, 0);
  const markupCents = roundCents((subtotalCents * fin.markupPct) / 100);
  const adjustCents = fin.adjustCents;
  const taxBase = subtotalCents + markupCents + adjustCents;
  const taxCents = roundCents((taxBase * fin.taxPct) / 100);
  const totalCents = taxBase + taxCents;
  const depositCents = roundCents((totalCents * fin.depositPct) / 100);
  return {
    lines: frozen,
    math: {
      subtotalCents, markupPct: fin.markupPct, markupCents,
      adjustCents, taxPct: fin.taxPct, taxCents, totalCents,
      depositPct: fin.depositPct, depositCents,
    },
  };
}
